/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The agent's own presence: what it is, and changing it.
 *
 * Shared by the dock and the console. Both need it because they are used at
 * different moments -- the dock when the agent is working somewhere else in
 * the CRM, the console when the call center is what they are looking at --
 * and an agent who can only change presence from the dock has to go find the
 * dock while their phone is not ringing.
 *
 * Engine-owned states (ringing, on_call, wrap_up) are rendered but never
 * offered: the server rejects them, and a menu that lets you pick something
 * the server refuses is worse than one that does not.
 */

import React, { useState } from "react";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import { AGENT_PRESENCE, PRESENCE_META } from "telephony/callStates";
import PresenceDot from "./PresenceDot";

const cc = colors.callCenter;

const ENGINE_OWNED = new Set(["ringing", "on_call", "wrap_up"]);

export default function PresenceControl({ presence, onChange, disabled, dense }) {
  const [anchor, setAnchor] = useState(null);
  const meta = PRESENCE_META[presence] || PRESENCE_META.offline;
  const locked = disabled || ENGINE_OWNED.has(presence);

  const label = (
    <MDBox display="flex" alignItems="center" gap={1}>
      <PresenceDot color={meta.color} size={dense ? 8 : 10} />
      <MDTypography variant="button" color="dark" fontWeight="medium">
        {meta.label}
      </MDTypography>
      {!locked ? <ExpandMore sx={{ fontSize: 18, color: cc.muted }} /> : null}
    </MDBox>
  );

  return (
    <>
      {locked ? (
        <Tooltip
          title={
            ENGINE_OWNED.has(presence)
              ? "Set by the call engine. It clears when the call does."
              : "Presence is unavailable right now."
          }
        >
          <MDBox display="inline-flex" sx={{ opacity: 0.75 }}>
            {label}
          </MDBox>
        </Tooltip>
      ) : (
        <MDBox
          component="button"
          type="button"
          aria-haspopup="listbox"
          aria-label={`Presence: ${meta.label}. Change it.`}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            border: "none",
            background: "none",
            p: 0,
            cursor: "pointer",
            font: "inherit",
          }}
        >
          {label}
        </MDBox>
      )}

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {AGENT_PRESENCE.map((p) => (
          <MenuItem
            key={p.value}
            selected={p.value === presence}
            onClick={async () => {
              setAnchor(null);
              await onChange(p.value);
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <PresenceDot color={p.color} size={8} />
              <MDTypography variant="button">{p.label}</MDTypography>
            </MDBox>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
